import { useState } from 'react'
import { eur, prixDemarque } from '@troc/shared'
import { DemandeDemarque } from './DemandeDemarque'

/**
 * Les baisses dues sur le ticket en cours, posées à la fin plutôt qu'à chaque
 * pièce.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Pourquoi à la fin
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Une fenêtre s'ouvrait autrefois dès qu'on encodait une pièce dont le contrat
 * prévoyait une baisse. Elle arrivait au mauvais moment : le vendeur encode dix
 * articles à la file, le client n'a encore rien demandé, et il fallait répondre
 * dix fois à une question qui ne se pose peut-être jamais.
 *
 * Elle se pose une seule fois, quand le ticket est complet et que le client
 * regarde le total — c'est là qu'il dit « il n'était pas moins cher, celui-là ? ».
 * Le vendeur ouvre alors cette fenêtre, voit d'un coup d'œil tout ce qui a droit
 * à une baisse, et accorde ce qu'il veut : tout, rien, ou une pièce en
 * particulier.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Ce que la baisse change, et ce qu'elle ne change pas
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Elle ne touche qu'à ce ticket : l'étiquette du rayon et le prix de l'article
 * en base restent ce qu'ils sont. Refaire l'étiquette est un geste séparé, qui
 * se fait depuis la fiche de l'article, appareil photo à la main.
 *
 * Le déposant, lui, est payé sur le prix de son contrat dans tous les cas :
 * accorder la baisse ne lui retire rien, cela réduit ce que la boutique garde.
 */

export interface LigneDiminuable {
  cle: number
  ref: string
  libelle: string
  mois: number
  decoteConseillee: number
  demarqueActive: boolean
  prixInitialCentimes: number
  prixCentimes: number
  quantite: number
}

/** Le prix que le contrat prévoit aujourd'hui pour cette pièce. */
export function prixContratDe(ligne: LigneDiminuable): number {
  return ligne.demarqueActive
    ? prixDemarque(ligne.prixInitialCentimes, ligne.mois)
    : ligne.prixInitialCentimes
}

/** A-t-elle droit à une baisse qu'on ne lui a pas encore accordée ? */
export function baisseDue(ligne: LigneDiminuable): boolean {
  return ligne.demarqueActive && prixContratDe(ligne) < ligne.prixCentimes
}

export function PanneauDiminutions({
  lignes,
  onPrix,
  onFermer,
}: {
  /** Toutes les lignes du ticket ; le tri se fait ici. */
  lignes: LigneDiminuable[]
  onPrix: (cle: number, prixCentimes: number) => void
  onFermer: () => void
}) {
  const [ajustee, setAjustee] = useState<LigneDiminuable | null>(null)

  const dues = lignes.filter(baisseDue)
  const totalEtiquette = dues.reduce((s, l) => s + l.prixCentimes * l.quantite, 0)
  const totalContrat = dues.reduce((s, l) => s + prixContratDe(l) * l.quantite, 0)

  if (ajustee) {
    return (
      <DemandeDemarque
        article={{
          ref: ajustee.ref,
          titre: ajustee.libelle,
          mois: ajustee.mois,
          decoteConseillee: ajustee.decoteConseillee,
          prixEtiquetteCentimes: ajustee.prixCentimes,
          prixDemarqueCentimes: prixContratDe(ajustee),
        }}
        libelleAnnuler="Laisser au prix de l’étiquette"
        onChoisir={(prixCentimes) => {
          onPrix(ajustee.cle, prixCentimes)
          setAjustee(null)
        }}
        onAnnuler={() => setAjustee(null)}
      />
    )
  }

  return (
    <div className="voile voile-centre" onMouseDown={onFermer}>
      <div
        className="panneau-diminutions"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="panneau-entete">
          <h4 style={{ margin: 0 }}>
            {dues.length === 0
              ? 'Aucune baisse à accorder'
              : `${dues.length} pièce${dues.length > 1 ? 's' : ''} a${dues.length > 1 ? 'ont' : ''} droit à une baisse`}
          </h4>
          <button type="button" className="btn btn-fantome" onClick={onFermer}>
            Fermer
          </button>
        </div>

        {dues.length === 0 ? (
          <div className="vide">
            Les pièces de ce ticket sont au prix prévu par leur contrat, ou trop récentes pour
            avoir baissé.
          </div>
        ) : (
          <>
            <div className="note">
              Le client peut demander le prix de son contrat. Accordez-le pièce par pièce, ou tout
              d’un coup — cela ne change ni l’étiquette du rayon ni ce que touchera le déposant,
              seulement ce que la boutique encaisse.
            </div>

            <table className="tableau">
              <thead>
                <tr>
                  <th>N°</th>
                  <th>Désignation</th>
                  <th>En rayon</th>
                  <th className="a-droite">Étiquette</th>
                  <th className="a-droite">Au contrat</th>
                  <th className="a-droite">Écart</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {dues.map((l) => {
                  const contrat = prixContratDe(l)
                  return (
                    <tr key={l.cle}>
                      <td className="ref">{l.ref}</td>
                      <td className="titre">{l.libelle}</td>
                      <td className="sourdine" style={{ whiteSpace: 'nowrap' }}>
                        {l.mois} mois · −{Math.round(l.decoteConseillee * 100)} %
                      </td>
                      <td className="a-droite chiffre">{eur(l.prixCentimes)}</td>
                      <td className="a-droite chiffre">{eur(contrat)}</td>
                      <td className="a-droite chiffre sourdine">
                        −{eur(l.prixCentimes - contrat)}
                      </td>
                      <td className="a-droite" style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => onPrix(l.cle, contrat)}
                        >
                          Accorder
                        </button>{' '}
                        <button type="button" className="btn btn-fantome" onClick={() => setAjustee(l)}>
                          Ajuster…
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </>
        )}

        {dues.length > 0 && (
          <div className="panneau-pied">
            <button type="button" className="btn btn-secondaire btn-large" onClick={onFermer}>
              Ne rien accorder
            </button>
            <button
              type="button"
              className="btn btn-primaire btn-large"
              style={{ flex: 1 }}
              onClick={() => {
                for (const l of dues) onPrix(l.cle, prixContratDe(l))
                onFermer()
              }}
            >
              Tout accorder — {eur(totalEtiquette)} → {eur(totalContrat)}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
